import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { featuredComics, latestUpdates, categories } from '@/mocks/comics';
import SEOHead from '@/components/feature/SEOHead';

interface ComicItem {
  id: number;
  title: string;
  category: string;
  author: string;
  cover: string;
  description?: string;
  rating: number;
  views?: number;
  chapters: number;
  isNew?: boolean;
  isHot?: boolean;
  updateDate?: string;
}

function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const id = Number(articleId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [articleId]);

  const allComics: ComicItem[] = [...featuredComics, ...latestUpdates];
  const comic = allComics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-book-read-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-600 mb-6">该漫画可能已被移除或不存在</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.id === comic.category);
  const relatedComics = allComics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://xxiut.cn',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: category?.name || '漫画分类',
            item: `https://xxiut.cn/category/${comic.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: comic.title,
            item: `https://xxiut.cn/article/${comic.id}`,
          },
        ],
      },
      {
        '@type': 'Article',
        headline: comic.title,
        description: comic.description || `${comic.title} - 高清韩国漫画免费在线阅读`,
        author: {
          '@type': 'Person',
          name: comic.author,
        },
        datePublished: comic.updateDate || '2026-01-01',
        dateModified: comic.updateDate || '2026-08-01',
        publisher: {
          '@type': 'Organization',
          name: '韩漫之家',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_韩漫之家`}
        description={comic.description || `${comic.title}是由${comic.author}创作的韩国漫画，评分${comic.rating}，已更新${comic.chapters}话，快来韩漫之家免费在线阅读！`}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫,高清漫画,韩漫之家`}
        canonicalUrl={`https://xxiut.cn/article/${comic.id}`}
        jsonLd={articleJsonLd}
      />

      <div className="pt-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-line mr-0.5"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <Link
              to={`/category/${comic.category}`}
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {category?.name || '漫画分类'}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">{comic.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="lg:w-[320px] flex-shrink-0">
              <div className="sticky top-24">
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    title={`${comic.title} - 韩漫之家高清韩国漫画`}
                  />
                  <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    {comic.isHot && (
                      <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs rounded-md font-medium">
                        热门推荐
                      </span>
                    )}
                    {comic.isNew && (
                      <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs rounded-md font-medium">
                        最新上架
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <article>
                <header className="mb-8">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Link
                      to={`/category/${comic.category}`}
                      className="px-3 py-1 bg-secondary-100 text-secondary-900 text-xs rounded-full font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      {category?.name || '未分类'}
                    </Link>
                    {comic.isHot && (
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">
                        热门
                      </span>
                    )}
                    {comic.isNew && (
                      <span className="px-3 py-1 bg-accent-100 text-accent-900 text-xs rounded-full font-medium">
                        新作
                      </span>
                    )}
                  </div>

                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    {comic.title}
                  </h1>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-user-line text-foreground-400"></i>
                      <strong className="text-foreground-600">作者：</strong>
                      <span className="text-foreground-700">{comic.author}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong className="text-foreground-600">评分：</strong>
                      <span className="text-foreground-700 font-medium">{comic.rating} / 10</span>
                    </div>
                    {comic.views && (
                      <div className="flex items-center gap-1.5">
                        <i className="ri-eye-line text-foreground-400"></i>
                        <strong className="text-foreground-600">阅读：</strong>
                        <span className="text-foreground-700">{comic.views.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-book-open-line text-foreground-400"></i>
                      <strong className="text-foreground-600">章节：</strong>
                      <span className="text-primary-500 font-medium">已更新 {comic.chapters} 话</span>
                    </div>
                    {comic.updateDate && (
                      <div className="flex items-center gap-1.5">
                        <i className="ri-calendar-line text-foreground-400"></i>
                        <strong className="text-foreground-600">最近更新：</strong>
                        <span className="text-foreground-700">{comic.updateDate}</span>
                      </div>
                    )}
                  </div>
                </header>

                <div className="bg-background-100 rounded-xl p-6 mb-8">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
                    作品简介
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {comic.description || `${comic.title}是由韩国漫画家${comic.author}创作的一部精彩漫画作品。故事围绕主要角色展开，情节紧凑，画风精美，深受读者喜爱。目前该作品已更新至第${comic.chapters}话，评分高达${comic.rating}分，是韩漫爱好者不可错过的佳作。`}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="flex items-center justify-center gap-2 px-8 py-3 bg-background-100 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-200 transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                </div>
              </article>

              {relatedComics.length > 0 && (
                <section className="mt-14">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {relatedComics.map((r) => (
                      <Link
                        key={`related-${r.id}`}
                        to={`/article/${r.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-2.5 bg-background-100">
                          <img
                            src={r.cover}
                            alt={r.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            title={`${r.title} - 韩漫之家高清韩国漫画`}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent pt-6 pb-2 px-2">
                            <div className="flex items-center gap-1 text-background-50 text-xs">
                              <i className="ri-star-fill text-accent-400 text-xs"></i>
                              <span>{r.rating}</span>
                            </div>
                          </div>
                        </div>
                        <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {r.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ArticlePage;